import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { authenticatedCall } from './auth';
import { PaginatedResponse, UserProduct, AdminUser } from './api.types';

export interface AdminReportedChat {
  id: string;
  chatId: string;
  reportedBy: AdminUser;
  reportedUser: AdminUser;
  product: UserProduct;
  reasons: string[];
  createdAt: string;
  messageCount: number;
  previousReportsForUser: number;
}

export interface AdminStatisticsSummary {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  currentPeriod: number;
  previousPeriod: number;
  growthPercentage: number;
}

export interface AdminStatistics {
  generatedAt: string;
  periodDays: number;
  products: AdminStatisticsSummary;
  users: AdminStatisticsSummary;
  productBreakdown: {
    active: number;
    pending: number;
    declined: number;
    expired: number;
    forSale: number;
    wanted: number;
  };
  userBreakdown: {
    active: number;
    blocked: number;
    verified: number;
    unverified: number;
  };
  timeline: Array<{
    date: string;
    products: number;
    users: number;
  }>;
}

export interface AdminApplicationSettings {
  announcementApprovalEnabled: boolean;
  announcementEmailSendingEnabled: boolean;
  updatedAt?: string | null;
}

export type IllegalContentNoticeStatus = 'RECEIVED' | 'UNDER_REVIEW' | 'ACTION_TAKEN' | 'REJECTED';

export interface AdminIllegalContentNotice {
  id: string;
  referenceCode: string;
  reporterName?: string | null;
  reporterEmail?: string | null;
  identityOmitted: boolean;
  contentUrl: string;
  explanation: string;
  status: IllegalContentNoticeStatus;
  decision?: string | null;
  createdAt: string;
  decidedAt?: string | null;
}

export const fetchIllegalContentNotices = async (params: {
  page?: number;
  size?: number;
  status?: IllegalContentNoticeStatus;
}): Promise<PaginatedResponse<AdminIllegalContentNotice>> => {
  const query = new URLSearchParams({
    page: String(params.page ?? 1),
    size: String(params.size ?? 10),
  });
  if (params.status) query.set('status', params.status);

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/illegal-content-notices?${query.toString()}`,
    { method: 'GET', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const updateIllegalContentNotice = async (
  noticeId: string,
  status: IllegalContentNoticeStatus,
  decision: string
): Promise<AdminIllegalContentNotice> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/illegal-content-notices/${noticeId}`,
    {
      method: 'PUT',
      headers: { ...getBaseHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, decision: decision.trim() || null }),
    }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const fetchAdminApplicationSettings = async (): Promise<AdminApplicationSettings> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/settings`,
    { method: 'GET', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const updateAdminApplicationSettings = async (
  settings: Pick<AdminApplicationSettings, 'announcementApprovalEnabled' | 'announcementEmailSendingEnabled'>
): Promise<AdminApplicationSettings> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/settings`,
    {
      method: 'PUT',
      headers: { ...getBaseHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const fetchAdminStatistics = async (days = 30): Promise<AdminStatistics> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/statistics?days=${days}`,
    { method: 'GET', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export type ContactMessageStatus = 'NEW' | 'READ' | 'RESOLVED';
export type EmailDeliveryStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface AdminContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: ContactMessageStatus;
  deliveryStatus: EmailDeliveryStatus;
  createdAt: string;
  sentAt?: string | null;
  readAt?: string | null;
  resolvedAt?: string | null;
  deliveryErrorCode?: string | null;
}

export interface ContactMessageSearchParams {
  page?: number;
  size?: number;
  status?: ContactMessageStatus;
  deliveryStatus?: EmailDeliveryStatus;
}

export interface ReportedChatSearchParams {
  page?: number;
  size?: number;
  productName?: string;
  reportedBy?: string;
  reportedUser?: string;
}

export interface AdminProductSearchParams {
  page?: number;
  size?: number;
  sortField?: string;
  order?: 'ASC' | 'DESC';
  status?: 'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED';
  productType?: 'PRODUCT' | 'DEMAND';
  name?: string | null;
  location?: {
    ids?: string[];
    range?: {
      range?: number;
    };
  };
  categoryId?: string | null;
  subCategoryIds?: string[];
  minPrice?: number | null;
  maxPrice?: number | null;
  negotiationType?: 'NEGOTIABLE' | 'NON_NEGOTIABLE' | null;
  username?: string | null;
}

export const searchAdminProducts = async (params: AdminProductSearchParams): Promise<PaginatedResponse<UserProduct>> => {
  const {
    page = 1,
    size = 20,
    sortField = 'CREATED_AT',
    order = 'DESC',
    status = 'PENDING',
    productType,
    name = null,
    location,
    categoryId = null,
    subCategoryIds = [],
    minPrice = null,
    maxPrice = null,
    negotiationType = null,
    username = null
  } = params;

  const requestBody: any = {
    ids: [],
    status: status,
    name,
    categoryId,
    subCategoryIds,
    minPrice,
    maxPrice,
    negotiationType,
    username
  };

  if (productType) {
    requestBody.productType = productType;
  }

  if (location) {
    requestBody.location = location;
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/products/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateAdminProductStatus = async (
  productId: string,
  status: 'ACTIVE' | 'DECLINED',
  reasons: string[] = []
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/products/${productId}/status`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status, reasons })
    }
  );

  if (!response.ok && response.status !== 204 && response.status !== 200) {
    await throwApiError(response);
  }
};

export interface ReportedProductSearchParams {
  page?: number;
  size?: number;
  sortField?: string;
  order?: 'ASC' | 'DESC';
  productType?: 'PRODUCT' | 'DEMAND';
  productStatus?: 'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED';
  name?: string | null;
  location?: {
    ids?: string[];
    range?: {
      range?: number;
    };
  };
  categoryId?: string | null;
  subCategoryIds?: string[];
  minPrice?: number | null;
  maxPrice?: number | null;
  negotiationType?: 'NEGOTIABLE' | 'NON_NEGOTIABLE' | null;
  username?: string | null;
}

export const searchReportedProducts = async (params: ReportedProductSearchParams): Promise<PaginatedResponse<UserProduct>> => {
  const {
    page = 1,
    size = 10,
    sortField = 'CREATED_AT',
    order = 'DESC',
    productType = 'PRODUCT',
    productStatus = 'ACTIVE',
    name = null,
    location,
    categoryId = null,
    subCategoryIds = [],
    minPrice = null,
    maxPrice = null,
    negotiationType = null,
    username = null
  } = params;

  const requestBody: any = {
    productType,
    productStatus,
    name,
    categoryId,
    subCategoryIds,
    minPrice,
    maxPrice,
    negotiationType,
    username
  };

  if (location) {
    requestBody.location = location;
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/products/reported/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export interface ReportedProductReasons {
  productId: string;
  reportedProductReasons: string[];
}

export const getReportedProductReasons = async (productId: string): Promise<ReportedProductReasons> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/products/reported/${productId}`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders(),
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export interface UserSearchParams {
  page?: number;
  size?: number;
  sortField?: string;
  order?: 'ASC' | 'DESC';
  id?: string;
  username?: string;
  email?: string;
  status?: 'ACTIVE' | 'BLOCKED';
}

export const searchUsers = async (params: UserSearchParams): Promise<PaginatedResponse<AdminUser>> => {
  const {
    page = 1,
    size = 10,
    sortField = 'CREATED_AT',
    order = 'DESC',
    id = '',
    username = '',
    email = '',
    status = 'ACTIVE'
  } = params;

  const requestBody: any = {
    id: id || null,
    username,
    email,
    status
  };

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/users/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const searchReportedUsers = async (params: UserSearchParams): Promise<PaginatedResponse<AdminUser>> => {
  const {
    page = 1,
    size = 10,
    sortField = 'CREATED_AT',
    order = 'DESC',
    id = '',
    username = '',
    email = '',
    status = 'ACTIVE'
  } = params;

  const requestBody: any = {
    id: id || null,
    username,
    email,
    status
  };

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/users/reported/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateUserStatus = async (
  userId: string,
  status: 'ACTIVE' | 'BLOCKED',
  reasons: string[] = []
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/users/${userId}/status`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status, reasons })
    }
  );

  if (!response.ok && response.status !== 204 && response.status !== 200) {
    await throwApiError(response);
  }
};

export const increaseUserAnnouncementLimit = async (
  userId: string,
  announcementLimit: number
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/users/${userId}/announcement-limit`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ announcementLimit })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const fetchReportedChats = async (
  params: ReportedChatSearchParams = {}
): Promise<PaginatedResponse<AdminReportedChat>> => {
  const {
    page = 1,
    size = 10,
    productName = '',
    reportedBy = '',
    reportedUser = '',
  } = params;

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/chats/reported/search?page=${page}&size=${size}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        productName: productName || null,
        reportedBy: reportedBy || null,
        reportedUser: reportedUser || null,
      })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchContactMessages = async (
  params: ContactMessageSearchParams = {}
): Promise<PaginatedResponse<AdminContactMessage>> => {
  const searchParams = new URLSearchParams({
    page: String(params.page ?? 1),
    size: String(params.size ?? 10),
  });
  if (params.status) searchParams.set('status', params.status);
  if (params.deliveryStatus) searchParams.set('deliveryStatus', params.deliveryStatus);

  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/contact-messages?${searchParams.toString()}`,
    { method: 'GET', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const updateContactMessageStatus = async (
  contactMessageId: string,
  status: ContactMessageStatus
): Promise<AdminContactMessage> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/contact-messages/${contactMessageId}/status`,
    {
      method: 'PUT',
      headers: { ...getBaseHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }
  );
  if (!response.ok) await throwApiError(response);
  return await response.json();
};

export const deleteContactMessage = async (contactMessageId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/administration/contact-messages/${contactMessageId}`,
    { method: 'DELETE', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
};

export type LegalDocumentType = 'TERMS' | 'PRIVACY' | 'COOKIES';
export type LegalDocumentStatus = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'ARCHIVED';

export interface AdminLegalDocument {
  id: string;
  documentType: LegalDocumentType;
  documentVersion: string;
  title: string;
  content: string;
  contentSha256: string;
  effectiveAt: string;
  requiresAcceptance: boolean;
  status: LegalDocumentStatus;
  changeSummary?: string | null;
  createdBy?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  acceptanceCount: number;
}

export interface LegalDocumentDraftRequest {
  documentType: LegalDocumentType;
  documentVersion: string;
  title: string;
  content: string;
  effectiveAt: string;
  requiresAcceptance: boolean;
  changeSummary?: string | null;
}

const legalDocumentRequest = async (
  path = '',
  options: RequestInit = { method: 'GET' }
): Promise<Response> => {
  const response = await authenticatedCall(`${API_BASE_URL}/administration/legal-documents${path}`, {
    ...options,
    headers: {
      ...getBaseHeaders(),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) await throwApiError(response);
  return response;
};

export const fetchAdminLegalDocuments = async (): Promise<AdminLegalDocument[]> =>
  (await legalDocumentRequest()).json();

export const createLegalDocumentDraft = async (
  request: LegalDocumentDraftRequest
): Promise<AdminLegalDocument> =>
  (await legalDocumentRequest('', { method: 'POST', body: JSON.stringify(request) })).json();

export const updateLegalDocumentDraft = async (
  documentId: string,
  request: LegalDocumentDraftRequest
): Promise<AdminLegalDocument> =>
  (await legalDocumentRequest(`/${documentId}`, { method: 'PUT', body: JSON.stringify(request) })).json();

export const publishLegalDocument = async (documentId: string): Promise<AdminLegalDocument> =>
  (await legalDocumentRequest(`/${documentId}/publish`, { method: 'POST' })).json();

export const returnLegalDocumentToDraft = async (documentId: string): Promise<AdminLegalDocument> =>
  (await legalDocumentRequest(`/${documentId}/return-to-draft`, { method: 'POST' })).json();

export const deleteLegalDocumentDraft = async (documentId: string): Promise<void> => {
  await legalDocumentRequest(`/${documentId}`, { method: 'DELETE' });
};
